import { z } from 'zod';
import { ActivityTypes, AppointmentFormats, AppointmentStatuses, Confidences, HandlingStatuses, MatchMethods, ReplyClasses, } from '../statuses';
import { LIMITS, csvArray, listQuerySchema, optionalTrimmed, trimmed, uuid } from './common';
const enumOf = (values) => z.enum(values);
export const inboundMessageSummarySchema = z.object({
    id: uuid,
    companyId: uuid.nullable(),
    companyName: z.string().nullable(),
    fromName: z.string().nullable(),
    fromEmail: z.string(),
    subject: z.string().nullable(),
    /** 一覧表示用の抜粋 */
    preview: z.string().nullable(),
    receivedAt: z.string(),
    replyClass: enumOf(ReplyClasses.values).nullable(),
    classConfidence: z.number().nullable(),
    isAutoReply: z.boolean(),
    handlingStatus: enumOf(HandlingStatuses.values),
    assignee: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    matchConfidence: enumOf(Confidences.values).nullable(),
    matchMethod: enumOf(MatchMethods.values).nullable(),
    matchCandidateCount: z.number().int(),
    hasAttachment: z.boolean(),
});
export const inboundMessageDetailSchema = inboundMessageSummarySchema.extend({
    toEmail: z.string().nullable(),
    bodyText: z.string().nullable(),
    bodyHtml: z.string().nullable(),
    /** 引用を除いた本文。表示の既定 */
    bodyStripped: z.string().nullable(),
    classRationale: z.string().nullable(),
    classCorrectedBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    matchCandidates: z.array(z.object({
        companyId: uuid,
        companyName: z.string(),
        reason: z.string(),
        confidence: enumOf(Confidences.values),
    })),
    sendAttempt: z
        .object({
        id: uuid,
        sentAt: z.string().nullable(),
        channel: z.string(),
        campaignId: uuid.nullable(),
        campaignName: z.string().nullable(),
        generatedMessageId: uuid.nullable(),
        subject: z.string().nullable(),
    })
        .nullable(),
    /** 本文から検出した日時。アポイント登録の初期値に使う */
    detectedDates: z.array(z.string()),
    companyStatus: z.string().nullable(),
    isSuppressed: z.boolean(),
});
export const inboxQuerySchema = listQuerySchema.extend({
    handlingStatus: csvArray(enumOf(HandlingStatuses.values)),
    replyClass: csvArray(enumOf(ReplyClasses.values)),
    assigneeId: z.string().max(40).optional(),
    companyId: uuid.optional(),
    /** 紐付け未確定のみ */
    unlinkedOnly: z.union([z.boolean(), z.string()]).optional(),
    from: z.string().optional(),
    to: z.string().optional(),
});
export const linkInboundSchema = z.object({
    companyId: uuid,
    /** 同一ドメインからの未紐付け受信も同じ企業に紐付ける */
    applyToSameDomain: z.boolean().default(false),
});
export const classifyInboundSchema = z.object({
    replyClass: enumOf(ReplyClasses.values),
    note: optionalTrimmed(LIMITS.reason),
});
export const updateInboundSchema = z.object({
    handlingStatus: enumOf(HandlingStatuses.values).optional(),
    assigneeId: uuid.nullish(),
});
export const replyInboundSchema = z.object({
    subject: trimmed(LIMITS.subject, '件名を入力してください'),
    body: trimmed(LIMITS.body, '本文を入力してください'),
    /** 送信後に対応状況を完了にする */
    markDone: z.boolean().default(false),
});
export const replyDraftSchema = z.object({
    tone: z.enum(['polite', 'concise']).default('polite'),
    /** 担当者が入力した候補日時。AI に空き時間を判断させない */
    proposedSlots: z.array(z.string().datetime()).max(5).optional(),
});
// ---------------------------------------------------------------- appointments
export const createAppointmentSchema = z
    .object({
    companyId: uuid,
    inboundMessageId: uuid.nullish(),
    scheduledAt: z.string().datetime({ message: '日時を指定してください' }),
    durationMinutes: z.number().int().min(5).max(600).default(60),
    format: enumOf(AppointmentFormats.values),
    location: optionalTrimmed(500),
    ownerId: uuid.optional(),
    participants: z
        .object({
        internal: z.array(z.string().trim().max(80)).max(20).optional(),
        external: z.array(z.string().trim().max(80)).max(20).optional(),
    })
        .optional(),
    purpose: optionalTrimmed(500),
    note: optionalTrimmed(LIMITS.note),
    /** 企業の営業状況を「アポ確定」に更新する（確認済みの場合） */
    updateCompanyStatus: z.boolean().default(true),
})
    .refine((v) => v.format !== 'visit' || !!v.location, {
    message: '訪問の場合は場所を入力してください',
    path: ['location'],
})
    .refine((v) => v.format !== 'online' || !!v.location, {
    message: 'オンラインの場合は会議 URL を入力してください',
    path: ['location'],
});
export const updateAppointmentSchema = z.object({
    scheduledAt: z.string().datetime().optional(),
    durationMinutes: z.number().int().min(5).max(600).optional(),
    format: enumOf(AppointmentFormats.values).optional(),
    location: optionalTrimmed(500),
    ownerId: uuid.optional(),
    purpose: optionalTrimmed(500),
    note: optionalTrimmed(LIMITS.note),
    status: enumOf(AppointmentStatuses.values).optional(),
});
export const appointmentResultSchema = z.object({
    status: z.enum(['held', 'postponed', 'canceled', 'no_show']),
    resultNote: optionalTrimmed(LIMITS.note),
    nextAction: optionalTrimmed(500),
    nextActionDueAt: z.string().datetime().nullish(),
});
export const appointmentSchema = z.object({
    id: uuid,
    companyId: uuid,
    companyName: z.string(),
    inboundMessageId: uuid.nullable(),
    scheduledAt: z.string(),
    durationMinutes: z.number().int(),
    format: enumOf(AppointmentFormats.values),
    location: z.string().nullable(),
    owner: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    participants: z.record(z.string(), z.unknown()).nullable(),
    purpose: z.string().nullable(),
    note: z.string().nullable(),
    status: enumOf(AppointmentStatuses.values),
    resultNote: z.string().nullable(),
    nextAction: z.string().nullable(),
    nextActionDueAt: z.string().nullable(),
    createdAt: z.string(),
});
export const appointmentsQuerySchema = listQuerySchema.extend({
    from: z.string().optional(),
    to: z.string().optional(),
    ownerId: z.string().max(40).optional(),
    status: csvArray(enumOf(AppointmentStatuses.values)),
    companyId: uuid.optional(),
});
// ---------------------------------------------------------------- activities
export const createActivitySchema = z
    .object({
    type: enumOf(ActivityTypes.values).default('note'),
    title: optionalTrimmed(200),
    body: optionalTrimmed(LIMITS.note),
    dueAt: z.string().datetime().nullish(),
    ownerId: uuid.optional(),
})
    .refine((v) => v.type !== 'next_action' || !!v.dueAt, {
    message: '次回アクションには期限が必要です',
    path: ['dueAt'],
})
    .refine((v) => !!v.title || !!v.body, {
    message: '内容を入力してください',
    path: ['body'],
});
export const updateActivitySchema = z.object({
    title: optionalTrimmed(200),
    body: optionalTrimmed(LIMITS.note),
    dueAt: z.string().datetime().nullish(),
    ownerId: uuid.nullish(),
    done: z.boolean().optional(),
});
export const activitySchema = z.object({
    id: uuid,
    companyId: uuid,
    companyName: z.string(),
    type: enumOf(ActivityTypes.values),
    title: z.string().nullable(),
    body: z.string().nullable(),
    dueAt: z.string().nullable(),
    doneAt: z.string().nullable(),
    isOverdue: z.boolean(),
    owner: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    createdBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    createdAt: z.string(),
});
export const activitiesQuerySchema = listQuerySchema.extend({
    type: csvArray(enumOf(ActivityTypes.values)),
    ownerId: z.string().max(40).optional(),
    companyId: uuid.optional(),
    /** 未完了のみ */
    openOnly: z.union([z.boolean(), z.string()]).optional(),
    overdueOnly: z.union([z.boolean(), z.string()]).optional(),
    dueBefore: z.string().optional(),
});
//# sourceMappingURL=inbox.js.map